import { IonSpinner } from '@ionic/react';
import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import { PaymentRequest } from '@stripe/stripe-js';
import React from 'react';

import { STRIPE_BUSINESS_ACCOUNT_COUNTRY } from '@/constants';
import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { useAccount } from '@/hooks/useAccount';
import { usePollingUser } from '@/hooks/usePollingUser';
import { AxonModule } from '@/modules/axon';
import { webSubscriptionsModule } from '@/modules/subscriptions';
import { webCampaign } from '@/modules/webCampaign';
import {
  StripeV2CoinsPrice,
  StripeV2SubscriptionPrice,
} from '@/services/stripe/dto';
import { createAxonPayloadForPlan } from '@/utils/axonUtils';
import { canLogEvents } from '@/utils/canLogEvents';
import { savePurchasedPlan } from '@/utils/purchasedPlanStorage';
import { sendAnalitycsAfterSubscription } from '@/utils/sendAnalitycsAfterSubscription';

import {
  MessageStyles,
  PaymentSystemButtonStyles,
} from './PaymentSystemButton.styles';

interface PaymentSystemButtonProps {
  plan: StripeV2SubscriptionPrice;
  billing_portal_link: string;
  disabled: boolean;
  clientSecret: string;
  selectedCoinPlan?: StripeV2CoinsPrice | null;
}

export const PaymentSystemButton: React.FC<PaymentSystemButtonProps> = ({
  plan,
  disabled,
  billing_portal_link,
  clientSecret,
  selectedCoinPlan,
}) => {
  const { user } = useAccount();
  const { startPolling } = usePollingUser();
  const userId = user?.id;

  const planConfig = webSubscriptionsModule.getPlanConfig(plan);

  const [stripePurchase, setStripePurchase] = React.useState({
    error: '',
    success: false,
    isSubmitting: false,
  });

  const stripe = useStripe();

  const [paymentRequest, setPaymentRequest] =
    React.useState<PaymentRequest | null>(null);
  const [isPaymentRequestAvailable, setIsPaymentRequestAvailable] =
    React.useState(false);

  const [message, setMessage] = React.useState('');

  const handleButtonClick = () => {
    if (!AxonModule.hasSentBeginCheckout()) {
      AxonModule.setBeginCheckoutSent();
      const payload = createAxonPayloadForPlan(plan);
      AxonModule.pushEvent(
        'begin_checkout',
        'axon_begin_checkout',
        payload,
        userId,
      );
    }
  };

  React.useEffect(() => {
    if (!stripe) return;

    const hideApplePayRecurringInfo = webCampaign.setup.paywallNotes?.includes(
      PAYWALL_NOTES.HIDE_APPLE_PAY_RECURRING_INFO,
    );

    // Create a PaymentRequest for Apple Pay
    const pr = stripe.paymentRequest({
      country: STRIPE_BUSINESS_ACCOUNT_COUNTRY,
      currency: 'usd',
      disableWallets: ['googlePay', 'browserCard', 'link'],
      total: {
        label: plan.label,
        amount: Math.round(plan.price * 100),
      },
      requestPayerName: false,
      requestPayerEmail: false,
      applePay: hideApplePayRecurringInfo
        ? undefined
        : {
            recurringPaymentRequest: {
              managementURL: billing_portal_link,
              paymentDescription: plan.label,
              regularBilling: {
                amount: Math.round(plan.price * 100),
                label: planConfig.display,
                recurringPaymentIntervalCount:
                  planConfig.recurringPaymentIntervalCount,
                recurringPaymentIntervalUnit:
                  planConfig.recurringPaymentIntervalUnit,
              },
            },
          },
    });

    // Check if the Payment Request button is available
    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
        setIsPaymentRequestAvailable(true);
      }
    });

    // Listen for the `paymentmethod` event (when the user completes payment)
    pr.on('paymentmethod', async (event) => {
      setStripePurchase((prev) => ({ ...prev, isSubmitting: true }));
      try {
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret, // Replace with your PaymentIntent's client secret
          {
            payment_method: event.paymentMethod.id,
          },
        );

        if (error) {
          setStripePurchase({
            error: `Payment failed: ${error.message}`,
            success: false,
            isSubmitting: false,
          });
          event.complete('fail');
        } else {
          setStripePurchase({
            error: '',
            success: true,
            isSubmitting: false,
          });
          startPolling();
          savePurchasedPlan(plan);
          if (canLogEvents()) {
            sendAnalitycsAfterSubscription(
              plan,
              selectedCoinPlan,
              paymentIntent?.id,
            );
          }
          event.complete('success');
        }
      } catch (err) {
        let message = '';
        if (err instanceof Error) {
          message = `Unexpected error: ${err.message}`;
        } else {
          message = 'Unexpected error occurred.';
        }
        setStripePurchase({
          error: message,
          success: false,
          isSubmitting: false,
        });
        event.complete('fail');
      }
    });

    // Handle unexpected errors or cancellations
    pr.on('cancel', () => {
      setMessage('Payment cancelled by the user.');
    });

    return () => {
      pr.off('paymentmethod');
      pr.off('cancel');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, userId]);

  return (
    <PaymentSystemButtonStyles disabled={disabled}>
      {isPaymentRequestAvailable && paymentRequest && (
        <PaymentRequestButtonElement
          options={{
            paymentRequest,
          }}
          onClick={handleButtonClick}
        />
      )}
      {message && <MessageStyles color="danger">{message}</MessageStyles>}
      {stripePurchase.error && (
        <MessageStyles color="danger">{stripePurchase.error}</MessageStyles>
      )}
      {stripePurchase.success && (
        <MessageStyles>
          We are processing your payment...
          <IonSpinner name="circles" />
        </MessageStyles>
      )}
    </PaymentSystemButtonStyles>
  );
};
